import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useMaintenance } from '../contexts/MaintenanceContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, Building2, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import MapCard from '../components/MapCard';
import AppLayout from '../components/AppLayout';
import { API_URL } from '../apiBase';

const getPhotoUrl = (photo_url) => {
  if (!photo_url) return null;
  // Si c'est du base64, retourner tel quel
  if (photo_url.startsWith('data:')) return photo_url;
  // Si c'est une URL externe, retourner tel quel
  if (photo_url.startsWith('http')) return photo_url;
  // Sinon, ajouter l'URL du backend
  return `${API_URL}${photo_url}`;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // If starts with 33, format as +33 x xx xx xx xx
  if (cleaned.startsWith('33') && cleaned.length === 11) {
    return `+33 ${cleaned[2]} ${cleaned.substring(3, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
  }
  
  // If starts with 0 and has 10 digits, convert to +33 format
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `+33 ${cleaned[1]} ${cleaned.substring(2, 4)} ${cleaned.substring(4, 6)} ${cleaned.substring(6, 8)} ${cleaned.substring(8, 10)}`;
  }
  
  // Return original if doesn't match expected format
  return phone;
};

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${id}`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Erreur lors du chargement des données utilisateur');
      setLoading(false);
    }
  };

  const getInitials = (nom, prenom) => {
    const n = nom?.charAt(0) || '';
    const p = prenom?.charAt(0) || '';
    return `${n}${p}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Utilisateur non trouvé</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
    <div className="min-h-full">
      {/* Page Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="bg-card border border-border rounded-md overflow-hidden relative">
          {/* Close Button - Top Right */}
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-muted/50 transition-colors group"
            title="Fermer"
            aria-label="Fermer la fiche utilisateur"
          >
            <X className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
          </button>
          
          {/* Profile Header */}
          <div className="p-8 border-b border-border">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={getPhotoUrl(user.photo_url)} />
                <AvatarFallback className="text-2xl">{getInitials(user.nom, user.prenom)}</AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {user.prenom} {user.nom}
                </h1>
                {user.poste && (
                  <p className="text-lg text-muted-foreground mb-3">{user.poste}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {user.societe && (
                    <span className="inline-block px-3 py-1 rounded bg-primary/10 text-primary font-medium text-sm">
                      {user.societe}
                    </span>
                  )}
                  {user.service && (
                    <span className="inline-block px-3 py-1 rounded bg-secondary text-secondary-foreground text-sm">
                      {user.service}
                    </span>
                  )}
                  {user.secteur && (
                    <span className="inline-block px-3 py-1 rounded bg-accent text-accent-foreground text-sm">
                      {user.secteur}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="p-8">
            <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Informations de contact
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {user.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email</p>
                    <a 
                      href={`mailto:${user.email}`}
                      className="font-medium text-primary hover:underline cursor-pointer"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {user.email}
                    </a>
                  </div>
                </div>
              )}
              
              {user.email_teams && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email Teams</p>
                    <a 
                      href={`mailto:${user.email_teams}`}
                      className="font-medium text-primary hover:underline cursor-pointer"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {user.email_teams}
                    </a>
                  </div>
                </div>
              )}
              
              {user.email_principal && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email Principal</p>
                    <a 
                      href={`mailto:${user.email_principal}`}
                      className="font-medium text-primary hover:underline cursor-pointer"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {user.email_principal}
                    </a>
                  </div>
                </div>
              )}
              
              {user.email_secondaire && user.email_secondaire !== user.email_principal && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email Secondaire</p>
                    <a 
                      href={`mailto:${user.email_secondaire}`}
                      className="font-medium text-primary hover:underline cursor-pointer"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {user.email_secondaire}
                    </a>
                  </div>
                </div>
              )}
              
              {user.telephone_mobile && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Téléphone professionnel Mobile</p>
                    <a 
                      href={`tel:${user.telephone_mobile}`}
                      className="font-medium text-primary hover:underline cursor-pointer"
                    >
                      {formatPhoneNumber(user.telephone_mobile)}
                    </a>
                  </div>
                </div>
              )}
              
              {user.telephone_fixe && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Téléphone professionnel Fixe</p>
                    <a 
                      href={`tel:${user.telephone_fixe}`}
                      className="font-medium text-primary hover:underline cursor-pointer"
                    >
                      {formatPhoneNumber(user.telephone_fixe)}
                    </a>
                  </div>
                </div>
              )}
              
              {user.societe && (
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Société</p>
                    <p className="font-medium">{user.societe}</p>
                  </div>
                </div>
              )}
              
              {user.service && (
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Secteur</p>
                    <p className="font-medium">{user.service}</p>
                  </div>
                </div>
              )}
              
              {user.secteur && (
                <div className="flex items-center gap-3">
                  <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Secteur</p>
                    <p className="font-medium">{user.secteur}</p>
                  </div>
                </div>
              )}
              
              {user.poste && (
                <div className="flex items-center gap-3">
                  <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Poste</p>
                    <p className="font-medium">{user.poste}</p>
                  </div>
                </div>
              )}
            </div>

            {(user.adresse || user.ville || user.code_postal || user.pays) && (
              <>
                <h2 className="text-xl font-semibold mt-8 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Adresse
                </h2>
                
                <div className="flex items-start gap-3 mb-6">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                  <div>
                    {user.adresse && <p>{user.adresse}</p>}
                    {(user.code_postal || user.ville) && (
                      <p>{user.code_postal} {user.ville}</p>
                    )}
                    {user.pays && <p>{user.pays}</p>}
                  </div>
                </div>

                <MapCard 
                  adresse={user.adresse}
                  ville={user.ville}
                  codePostal={user.code_postal}
                  pays={user.pays}
                  nom={user.nom}
                  prenom={user.prenom}
                />
              </>
            )}

            {user.date_embauche && (
              <>
                <h2 className="text-xl font-semibold mt-8 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Informations professionnelles
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Date d'embauche</p>
                    <p className="font-medium">{user.date_embauche}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
    </AppLayout>
  );
};

export default UserDetailPage;